import { useId } from 'react';
import { RANK_PIPS, rankOf, suitOf, type Card } from '@cribbage/engine';
import { cardBackAsset, DEFAULT_CARD_BACK, type CardBack } from '~/store/cardBacks.js';

/**
 * The art on a playing card, drawn as SVG.
 *
 * Faces are purely geometric: every dimension is a fraction of the 250×350
 * viewBox, so one drawing covers a hand card and a hero card. Face color comes
 * from the tokens in `styles.css`. A face-down card instead clips the player's
 * selected 5:7 backing image into the same cut edge.
 *
 * Suits are paths, not font glyphs: ♠♥♦♣ pick up emoji presentation on iOS,
 * and this game ships as a Capacitor app.
 */

const W = 250;
const H = 350;
const RADIUS = W * 0.08;

/** Corner index: rank over suit, the block a fanned hand is read by. */
const INDEX_X = 25;
const INDEX_RANK_SIZE = H * 0.19;
const INDEX_RANK_Y = 42;
const INDEX_SUIT_SIZE = H * 0.13;
const INDEX_SUIT_Y = 92;
/** "10" is condensed to a single index's width rather than shrunk. */
const INDEX_TEN_WIDTH = 44;

/** The classic pip grid, ported from the atlas the 3D table used. */
const PIP_X = [0.33, 0.5, 0.67].map((f) => f * W);
const PIP_Y = [0.27, 0.385, 0.5, 0.615, 0.73].map((f) => f * H);
const PIP_SIZE = H * 0.14;

type PipCell = readonly [column: number, row: number];

const PIP_LAYOUTS: Readonly<Record<number, readonly PipCell[]>> = {
  2: [
    [1, 0],
    [1, 4],
  ],
  3: [
    [1, 0],
    [1, 2],
    [1, 4],
  ],
  4: [
    [0, 0],
    [2, 0],
    [0, 4],
    [2, 4],
  ],
  5: [
    [0, 0],
    [2, 0],
    [1, 2],
    [0, 4],
    [2, 4],
  ],
  6: [
    [0, 0],
    [2, 0],
    [0, 2],
    [2, 2],
    [0, 4],
    [2, 4],
  ],
  7: [
    [0, 0],
    [2, 0],
    [1, 1],
    [0, 2],
    [2, 2],
    [0, 4],
    [2, 4],
  ],
  8: [
    [0, 0],
    [2, 0],
    [1, 1],
    [0, 2],
    [2, 2],
    [1, 3],
    [0, 4],
    [2, 4],
  ],
  9: [
    [0, 0],
    [2, 0],
    [0, 1],
    [2, 1],
    [1, 2],
    [0, 3],
    [2, 3],
    [0, 4],
    [2, 4],
  ],
  10: [
    [0, 0],
    [2, 0],
    [1, 1],
    [0, 1],
    [2, 1],
    [0, 3],
    [2, 3],
    [1, 3],
    [0, 4],
    [2, 4],
  ],
};

/** Ace: one large pip inside engraved rings. */
const ACE_SIZE = H * 0.34;
const ACE_RING_INNER = H * 0.26;
const ACE_RING_OUTER = H * 0.29;

/**
 * Court: a double-headed bust inside an inset panel, the way a real deck draws
 * one — the top half is the only half authored, and the same nodes are rotated
 * 180° about the centre for the bottom.
 */
const COURT_INSET_X = W * 0.14;
const COURT_INSET_Y = H * 0.14;

const CENTRE_X = W / 2;
const CENTRE_Y = H / 2;

function n(value: number): string {
  return String(Math.round(value * 100) / 100);
}

export interface CardSvgProps {
  readonly card: Card | null;
  readonly fourColour?: boolean;
  /** The image on a face-down card. Ignored when `card` is present. */
  readonly cardBack?: CardBack;
}

export function CardSvg({
  card,
  fourColour = false,
  cardBack = DEFAULT_CARD_BACK,
}: CardSvgProps): React.JSX.Element {
  // Paint-server ids must be unique per instance: a shared id that unmounts
  // takes every other card's gradient or clip with it on some engines.
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  return (
    <svg className="block size-full" viewBox={`0 0 ${W} ${H}`} aria-hidden="true" focusable="false">
      {card === null ? (
        <Back uid={uid} cardBack={cardBack} />
      ) : (
        <Face uid={uid} card={card} fourColour={fourColour} />
      )}
    </svg>
  );
}

function Face({
  uid,
  card,
  fourColour,
}: {
  readonly uid: string;
  readonly card: Card;
  readonly fourColour: boolean;
}): React.JSX.Element {
  const rank = rankOf(card);
  const suit = suitOf(card);
  const paper = `pc-${uid}-paper`;
  return (
    <g>
      {/* Two stops, top to bottom: the chip white, settling into the stock.
          The white line that used to run along the top inner edge is not
          coming back with it — that was a lit edge, and the run down the face
          is the whole of the depth a card is allowed. */}
      <defs>
        <linearGradient className="card-paper-gradient" id={paper} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" />
          <stop offset="1" />
        </linearGradient>
      </defs>
      <rect
        x="0.5"
        y="0.5"
        width={W - 1}
        height={H - 1}
        rx={RADIUS}
        fill={`url(#${paper})`}
        stroke="rgba(20,17,14,0.25)"
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      <g color={suitColour(suit, fourColour)}>
        <Index rank={rank} suit={suit} />
        <Index rank={rank} suit={suit} flip />
        {rank >= 11 ? (
          <Court rank={rank} suit={suit} />
        ) : rank === 1 ? (
          <Ace suit={suit} />
        ) : (
          <Pips rank={rank} suit={suit} />
        )}
      </g>
    </g>
  );
}

/**
 * Diamonds and clubs get their own hues in the four-colour deck. Red/black
 * alone is a poor discriminator for a lot of players, and suit decides flushes
 * and his nobs.
 *
 * Exported because the showcase labels its suit picker in the deck's own
 * colours, and a second copy of this rule would be a page that can disagree
 * with the cards it is describing.
 */
export function suitColour(suit: number, fourColour: boolean): string {
  if (suit === 2) return 'var(--card-face-red)';
  if (suit === 1) return fourColour ? 'var(--blue)' : 'var(--card-face-red)';
  if (suit === 0) return fourColour ? 'var(--green)' : 'var(--black)';
  return 'var(--black)';
}

function Index({
  rank,
  suit,
  flip = false,
}: {
  readonly rank: number;
  readonly suit: number;
  readonly flip?: boolean;
}): React.JSX.Element {
  const label = indexLabel(rank);
  const wide = label.length > 1;
  return (
    <g
      transform={
        flip
          ? `translate(${n(W - INDEX_X)} ${n(H - INDEX_RANK_Y)}) rotate(180)`
          : `translate(${n(INDEX_X)} ${n(INDEX_RANK_Y)})`
      }
    >
      {/* `lining-nums` is inert on Cabinet Grotesk, measured 2026-08-27: the
          face ships no numeral features at all. It was written against Rowan,
          which did default to old-style figures, and it stays — sweeping the
          inert numeral declarations is a decision already taken and recorded in
          the `components_client.md` numerals row. */}
      <text
        className="font-(family-name:--card-face-font) font-bold tracking-tight lining-nums"
        x="0"
        y="0"
        fontSize={n(INDEX_RANK_SIZE)}
        textAnchor="middle"
        dominantBaseline="central"
        fill="currentColor"

        textLength={wide ? INDEX_TEN_WIDTH : undefined}
        lengthAdjust={wide ? 'spacingAndGlyphs' : undefined}
      >
        {label}
      </text>
      <SuitShape suit={suit} cx={0} cy={INDEX_SUIT_Y - INDEX_RANK_Y} size={INDEX_SUIT_SIZE} />
    </g>
  );
}

/** The index says "10", not the "T" `cardCode` packs a rank into. */
function indexLabel(rank: number): string {
  return RANK_PIPS[rank - 1] ?? '?';
}

function Pips({ rank, suit }: { readonly rank: number; readonly suit: number }): React.JSX.Element {
  return (
    <>
      {(PIP_LAYOUTS[rank] ?? []).map(([column, row], index) => {
        const cx = PIP_X[column] ?? CENTRE_X;
        const cy = PIP_Y[row] ?? CENTRE_Y;
        return (
          <SuitShape
            key={index}
            suit={suit}
            cx={cx}
            cy={cy}
            size={PIP_SIZE}
            // Real cards mirror the lower half, so a hand reads from either end.
            flip={cy > CENTRE_Y}
          />
        );
      })}
    </>
  );
}

function Ace({ suit }: { readonly suit: number }): React.JSX.Element {
  return (
    <g>
      {suit === 3 ? <PegTrack /> : <EngravedRings />}
      <SuitShape suit={suit} cx={CENTRE_X} cy={CENTRE_Y} size={ACE_SIZE} />
    </g>
  );
}

function EngravedRings(): React.JSX.Element {
  return (
    <g
      fill="none"
      stroke="currentColor"
      strokeOpacity="0.2"
      strokeWidth={1}
      vectorEffect="non-scaling-stroke"
    >
      <circle cx={CENTRE_X} cy={CENTRE_Y} r={n(ACE_RING_INNER)} />
      <circle cx={CENTRE_X} cy={CENTRE_Y} r={n(ACE_RING_OUTER)} />
    </g>
  );
}

/**
 * The ace of spades wears the house mark: the board's peg track curled into a
 * medallion, in ink instead of brass.
 */
function PegTrack(): React.JSX.Element {
  const radius = (ACE_RING_INNER + ACE_RING_OUTER) / 2;
  const holes = Array.from({ length: 24 }, (_, i) => {
    const angle = (i / 24) * Math.PI * 2 - Math.PI / 2;
    return { x: CENTRE_X + Math.cos(angle) * radius, y: CENTRE_Y + Math.sin(angle) * radius };
  });
  return (
    <g>
      <g
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.2"
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      >
        <circle cx={CENTRE_X} cy={CENTRE_Y} r={n(ACE_RING_INNER)} />
        <circle cx={CENTRE_X} cy={CENTRE_Y} r={n(ACE_RING_OUTER)} />
      </g>
      <g fill="currentColor" fillOpacity="0.28">
        {holes.map((hole, index) => (
          <circle key={index} cx={n(hole.x)} cy={n(hole.y)} r="3.2" />
        ))}
      </g>
    </g>
  );
}

function Court({
  rank,
  suit,
}: {
  readonly rank: number;
  readonly suit: number;
}): React.JSX.Element {
  return (
    <g>
      <rect
        x={n(COURT_INSET_X)}
        y={n(COURT_INSET_Y)}
        width={n(W - COURT_INSET_X * 2)}
        height={n(H - COURT_INSET_Y * 2)}
        rx="8"
        fill="currentColor"
        fillOpacity="0.06"
        stroke="currentColor"
        strokeOpacity="0.35"
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      <CourtHalf rank={rank} suit={suit} />
      <g transform={`rotate(180 ${CENTRE_X} ${CENTRE_Y})`}>
        <CourtHalf rank={rank} suit={suit} />
      </g>
      {/* The seam the two halves meet on — drawn last so neither robe crosses it. */}
      <line
        x1={n(COURT_INSET_X)}
        y1={CENTRE_Y}
        x2={n(W - COURT_INSET_X)}
        y2={CENTRE_Y}
        stroke="currentColor"
        strokeOpacity="0.35"
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
    </g>
  );
}

/**
 * One bust, drawn upright in the top half of the panel.
 *
 * Everything is authored between the panel's top edge and the centre line, so
 * the rotated copy lands exactly on the other half without a second set of
 * coordinates. Strokes here deliberately scale with the card — this is
 * figure art, not a hairline rule, and a 2.5-unit line that stays 1px at hero
 * size would leave the courts looking unfinished next to the pips.
 */
function CourtHalf({
  rank,
  suit,
}: {
  readonly rank: number;
  readonly suit: number;
}): React.JSX.Element {
  return (
    <g vectorEffect="none" strokeLinecap="round" strokeLinejoin="round">
      {/* Neck and robe first: the head sits on them, the regalia in front. */}
      <path d="M115 108h20v28h-20z" fill="var(--card-paper)" />
      {/*
        A torso rather than a shoulder line. This half and its rotated twin
        meet base to base at the seam, so whatever shape reaches y=175 is
        doubled: two shallow arcs pair into a lens, and the card ends up a
        flying saucer with a head on top. It has to be taller than it is wide.
      */}
      <path
        d="M72 175v-15c0-22 12-33 28-36h50c16 3 28 14 28 36v15z"
        fill="currentColor"
        fillOpacity="0.22"
        stroke="currentColor"
        strokeWidth={2.5}
      />
      {/* Arms, as the two folds where the sleeves break from the body. */}
      <g fill="none" stroke="currentColor" strokeWidth={2} strokeOpacity="0.55">
        <path d="M98 175v-15c-1-14 3-24 9-30" />
        <path d="M152 175v-15c1-14-3-24-9-30" />
      </g>
      {/* The placket, and a line of court gold down it. */}
      <path
        d="M125 132l-14 12 14 31 14-31z"
        fill="currentColor"
        fillOpacity="0.35"
        stroke="var(--court-gold)"
        strokeWidth={2}
      />
      {/* A suit brooch at the throat — a court says its suit twice. */}
      <SuitShape suit={suit} cx={125} cy={153} size={17} />
      <path
        d="M95 138q7.5-12 15 0 7.5-12 15 0 7.5-12 15 0 7.5-12 15 0"
        fill="var(--card-paper)"
        stroke="currentColor"
        strokeWidth={2.5}
      />

      <CourtDevice rank={rank} />

      <ellipse
        cx={125}
        cy={96}
        rx={19}
        ry={22}
        fill="var(--card-paper)"
        stroke="currentColor"
        strokeWidth={2.5}
      />
      <CourtFace rank={rank} />
      <CourtCrown rank={rank} />
    </g>
  );
}

/**
 * The face, such as it is at 84 pixels wide: eyes, a nose, and whatever the
 * rank wears under its crown. A king's beard stands in for his mouth.
 */
function CourtFace({ rank }: { readonly rank: number }): React.JSX.Element {
  return (
    <g fill="currentColor" stroke="currentColor">
      {rank === 13 && (
        // Beard and moustache, hung off the cheekbones.
        <path
          d="M107 100c0 20 8 32 18 32s18-12 18-32c-4 9-10 13-18 13s-14-4-18-13z"
          fillOpacity="0.9"
          strokeWidth={2}
        />
      )}
      {rank === 12 && (
        // Hair, falling past the jaw to the collar on both sides.
        <path
          d="M107 84c-8 14-10 30-7 43l9-3c-2-11-1-24 4-33zM143 84c8 14 10 30 7 43l-9-3c2-11 1-24-4-33z"
          fillOpacity="0.85"
          strokeWidth={1.5}
        />
      )}
      {rank === 11 && (
        // A single curl escaping the cap.
        <path
          d="M141 86c7 8 8 19 4 26-4 5-9 6-13 4 5-1 9-4 10-8 2-7-1-16-5-22z"
          fillOpacity="0.85"
          strokeWidth={1.5}
        />
      )}
      <circle cx={117.5} cy={91} r={2.2} stroke="none" />
      <circle cx={132.5} cy={91} r={2.2} stroke="none" />
      <path d="M125 95v8l4 2" fill="none" strokeWidth={2} />
      {rank !== 13 && <path d="M119.5 110q5.5 4 11 0" fill="none" strokeWidth={2} />}
    </g>
  );
}

/** What the figure holds: a king's sword, a queen's rose, a jack's pike. */
function CourtDevice({ rank }: { readonly rank: number }): React.JSX.Element {
  return (
    <g fill="var(--court-gold)" stroke="currentColor" strokeWidth={2} strokeOpacity="0.7">
      {rank === 13 && (
        <>
          <path d="M62 50l5 17v42H57V67z" fill="var(--card-paper)" />
          <rect x={46} y={109} width={32} height={7} rx={3} />
          <rect
            x={57}
            y={116}
            width={10}
            height={17}
            rx={3}
            fill="currentColor"
            fillOpacity="0.55"
          />
          <circle cx={62} cy={138} r={6} />
        </>
      )}
      {rank === 12 && (
        <>
          <path d="M62 175c-3-26-2-50 0-73" fill="none" strokeOpacity="0.85" />
          <path
            d="M62 124c-10 2-16-4-15-13 8-1 14 4 15 13zM62 146c10 2 16-4 15-13-8-1-14 4-15 13z"
            fill="currentColor"
            fillOpacity="0.45"
          />
          <g>
            <circle cx={62} cy={76} r={7.5} />
            <circle cx={51} cy={83} r={6.5} />
            <circle cx={73} cy={83} r={6.5} />
            <circle cx={55} cy={95} r={6.5} />
            <circle cx={69} cy={95} r={6.5} />
          </g>
          <circle cx={62} cy={86} r={4.5} fill="currentColor" stroke="none" />
        </>
      )}
      {rank === 11 && (
        <>
          <rect
            x={58}
            y={62}
            width={7}
            height={113}
            rx={3}
            fill="currentColor"
            fillOpacity="0.45"
          />
          <path d="M62 46l8 16-8 9-8-9z" />
          <path d="M66 78c13 2 18 12 13 21l-13-7z" />
        </>
      )}
    </g>
  );
}

/** King a crown, queen a coronet, jack a soft cap — court gold, and worn. */
function CourtCrown({ rank }: { readonly rank: number }): React.JSX.Element {
  return (
    <g fill="var(--court-gold)" stroke="currentColor" strokeWidth={2.5}>
      {rank === 13 && (
        <>
          <path d="M101 70l8-20 8 14 8-20 8 20 8-14 8 20z" />
          <rect x={99} y={68} width={52} height={11} rx={4} />
          <g stroke="none">
            <circle cx={109} cy={47} r={3.4} />
            <circle cx={125} cy={43} r={3.8} />
            <circle cx={141} cy={47} r={3.4} />
          </g>
        </>
      )}
      {rank === 12 && (
        <>
          <path d="M103 72q22-18 44 0z" />
          <rect x={101} y={70} width={48} height={9} rx={4} />
          <g stroke="none" fill="var(--court-gold-bright)">
            <circle cx={107} cy={67} r={3} />
            <circle cx={116} cy={60} r={3} />
            <circle cx={125} cy={57} r={3.4} />
            <circle cx={134} cy={60} r={3} />
            <circle cx={143} cy={67} r={3} />
          </g>
        </>
      )}
      {rank === 11 && (
        <>
          {/* The feather first — it tucks under the band rather than onto it. */}
          <path
            d="M146 58c10-14 20-22 30-27-6 11-14 22-24 32z"
            fill="currentColor"
            fillOpacity="0.5"
          />
          <path d="M104 72c-3-18 9-26 23-26s21 10 20 26z" fill="currentColor" fillOpacity="0.35" />
          <rect x={101} y={68} width={48} height={10} rx={4} />
        </>
      )}
    </g>
  );
}

function Back({
  uid,
  cardBack,
}: {
  readonly uid: string;
  readonly cardBack: CardBack;
}): React.JSX.Element {
  const clip = `pc-${uid}-back-clip`;
  return (
    <g>
      <defs>
        <clipPath id={clip}>
          <rect x="0" y="0" width={W} height={H} rx={RADIUS} />
        </clipPath>
      </defs>
      <rect width={W} height={H} rx={RADIUS} fill="var(--card-back-ground)" />
      <image
        href={cardBackAsset(cardBack)}
        width={W}
        height={H}
        preserveAspectRatio="xMidYMid slice"
        clipPath={`url(#${clip})`}
      />
      <rect
        x="0.5"
        y="0.5"
        width={W - 1}
        height={H - 1}
        rx={RADIUS}
        fill="none"
        stroke="rgba(9,22,16,0.6)"
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
    </g>
  );
}

function SuitShape({
  suit,
  cx,
  cy,
  size,
  flip = false,
}: {
  readonly suit: number;
  readonly cx: number;
  readonly cy: number;
  readonly size: number;
  readonly flip?: boolean;
}): React.JSX.Element {
  const scale = size / 100;
  const transform = `translate(${n(cx)} ${n(cy)})${flip ? ' rotate(180)' : ''} scale(${n(scale)}) translate(-50 -50)`;
  return (
    <g transform={transform} fill="currentColor">
      {suit === 0 ? (
        <>
          <circle cx="50" cy="28" r="21" />
          <circle cx="24" cy="58" r="21" />
          <circle cx="76" cy="58" r="21" />
          <path d="M50 56C53 74 58 88 67 96H33C42 88 47 74 50 56Z" />
        </>
      ) : suit === 1 ? (
        <path d="M50 4C62 24 74 38 90 50C74 62 62 76 50 96C38 76 26 62 10 50C26 38 38 24 50 4Z" />
      ) : suit === 2 ? (
        <path d="M50 90C22 68 6 52 6 34C6 18 18 8 31 8C40 8 47 13 50 20C53 13 60 8 69 8C82 8 94 18 94 34C94 52 78 68 50 90Z" />
      ) : (
        <path d="M50 6C50 6 90 32 90 56C90 70 80 78 69 78C61 78 55 74 52 68C52 80 57 90 66 96H34C43 90 48 80 48 68C45 74 39 78 31 78C20 78 10 70 10 56C10 32 50 6 50 6Z" />
      )}
    </g>
  );
}
